import { useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { connect, ConnectedProps } from 'react-redux';
import { captureMessage } from '@tink/core/src/sentry';
import { userAgent } from '@tink/tink-link-core/src/user-agent-detector';
import { SetTimer, timerUtils } from '../../../../utilities/timer';
import { useConfiguration } from '../../../../components/ApplicationProviders/ConfigurationProvider/ConfigurationProvider';
import { ThirdPartyRedirectButton } from '../../../../components/screens/AwaitingCredentialsScreen/organisms/AwaitingThirdpartyAuthenticationContainer/molecules/ThirdPartyRedirectButton/ThirdPartyRedirectButton';
import { ProviderEntity } from '../../../../models/provider';
import { AwaitingScenario, Credentials, CredentialsEntity } from '../../../../models/credentials';
import { setRedirectedCredentialsId as setRedirectedCredentialsIdImport } from '../../../../stores/app-state-store';
import { AppState } from '../../../../stores/redux-store';
import { PageView } from '../../../../models/analytics';
import {
  ProviderRedirectInstructions as ProviderRedirectInstructionsImport,
  ProviderRedirectInstructionsOwnProps,
} from '../../../../core/screens/ProviderRedirectInstructions/ProviderRedirectInstructions';

import {
  AwaitingTppPendingScreen as AwaitingTppPendingScreenImport,
  AwaitingTppPendingScreenProps,
} from '../../../../components/screens/AwaitingCredentialsScreen/organisms/AwaitingMobileBankIdAuthenticationContainer/molecules/PendingThirdPartyAuthentication/AwaitingTppPendingScreen';
import { messages as awaitingThirdPartyMessages } from '../../../../components/screens/AwaitingCredentialsScreen/organisms/AwaitingThirdpartyAuthenticationContainer/molecules/AwaitingThirdpartyAuthorization/messages';
import { shouldAutoRedirectToThirdParty } from '../../../../components/screens/AwaitingCredentialsScreen/organisms/AwaitingThirdpartyAuthenticationContainer/AuthAwaitingThirdpartyAuthenticationContainer/AuthAwaitingThirdpartyAuthenticationContainer';
import { LoadingOverlay } from '../../../../core/screens/LoadingOverlay/LoadingOverlay';
import {
  UseThirdPartyRedirector,
  useThirdPartyRedirector as useThirdPartyRedirectorImport,
} from '../../../../utilities/thirdparty-redirector/thirdparty-redirector';
import { LegalDisclaimer } from '../../../../components/molecules/LegalDisclaimer/LegalDisclaimer';
import { useAnalytics } from '../../../../core/Analytics/AnalyticsProvider';
import { getAutoRedirectMobileQueryParamAnalytic } from '../../../../components/screens/AwaitingCredentialsScreen/organisms/AwaitingThirdpartyAuthenticationContainer/AuthAwaitingThirdpartyAuthenticationContainer/getAutoRedirectMobileQueryParamAnalytic';
import { BackButton } from '../../../../components/organisms/Header/HeaderMolecule';
import { useCredentialsCopy } from '../../../../components/screens/SubmitCredentials/hooks/useCredentialsCopy';

export type PayAwaitingThirdpartyAuthenticationContainerOwnProps = {
  isDesktop?: boolean;
  setTimer?: SetTimer;
  provider: ProviderEntity;
  credentials: CredentialsEntity;
  allowExit?: boolean;
  footer?: React.ReactNode;
  awaitingScenario: AwaitingScenario;
  useThirdPartyRedirector?: UseThirdPartyRedirector;
  AwaitingTppPendingScreen?: React.ComponentType<AwaitingTppPendingScreenProps>;
  ProviderRedirectInstructions?: React.ComponentType<ProviderRedirectInstructionsOwnProps>;
} & BackButton;

export type PayAwaitingThirdpartyAuthenticationContainerProps =
  PayAwaitingThirdpartyAuthenticationContainerOwnProps & PropsFromRedux;

export const PayAwaitingThirdpartyAuthenticationContainerPresenter: React.FC<
  PayAwaitingThirdpartyAuthenticationContainerProps
> = ({
  userHintId,
  credentials,
  provider,
  allowExit,
  footer,
  isDesktop = userAgent.isDesktop,
  setTimer = timerUtils.setTimer,
  onBack,
  setRedirectedCredentialsId,
  useThirdPartyRedirector = useThirdPartyRedirectorImport,
  AwaitingTppPendingScreen = AwaitingTppPendingScreenImport,
  ProviderRedirectInstructions = ProviderRedirectInstructionsImport,
}) => {
  const [awaitingAuth, setAwaitingAuth] = useState(false);
  const [showPendingScreen, setShowPendingScreen] = useState(isDesktop);
  const { data } = useConfiguration();
  const { tinklinkAnalytics } = useAnalytics();
  const { thirdPartyRedirector } = useThirdPartyRedirector(credentials);
  const shouldAutoRedirect = shouldAutoRedirectToThirdParty({
    isDesktop,
    configuration: data,
    thirdPartyRedirector,
  });
  const providerDisplayName = provider.displayName;
  const { heading } = useCredentialsCopy();

  useEffect(() => {
    if (shouldAutoRedirect) {
      setRedirectedCredentialsId(credentials.id);
      thirdPartyRedirector?.navigate();

      setTimer(() => {
        // Show "PendingThirdPartyAuthentication" screen to allow user navigate to bank manually if automatic redirect fails.
        setAwaitingAuth(true);
      }, 6000);
    }
  }, [
    credentials.id,
    setRedirectedCredentialsId,
    shouldAutoRedirect,
    thirdPartyRedirector,
    setTimer,
  ]);

  useEffect(() => {
    const interactionEvent = getAutoRedirectMobileQueryParamAnalytic();

    if (interactionEvent) tinklinkAnalytics.sendInteractionEventOnce(interactionEvent);
  }, [tinklinkAnalytics]);

  if (!Credentials.isAwaitingThirdPartyAppAuthentication(credentials)) {
    captureMessage(
      'PayAwaitingThirdpartyAuthentication was rendered with credentials in unexpected state',
      {
        custom: {
          credentialsStatus: credentials?.status || 'N/A',
        },
      }
    );

    return null;
  }

  if (shouldAutoRedirect && !awaitingAuth) {
    return <LoadingOverlay />;
  }

  if (showPendingScreen) {
    return (
      <AwaitingTppPendingScreen
        providerName={providerDisplayName}
        onBack={() => setShowPendingScreen(false)}
        credentials={credentials}
      />
    );
  }

  return (
    <ProviderRedirectInstructions
      onBack={onBack}
      provider={provider}
      allowExit={allowExit}
      pageView={PageView.AWAITING_THIRD_PARTY_AUTHENTICATION_SCREEN}
      primaryButton={
        <ThirdPartyRedirectButton
          color="primary"
          variant="contained"
          pageView={PageView.AWAITING_THIRD_PARTY_AUTHENTICATION_SCREEN}
          credentials={credentials}
        >
          <FormattedMessage
            {...awaitingThirdPartyMessages.buttonText}
            values={{ providerName: provider.displayName }}
          />
        </ThirdPartyRedirectButton>
      }
      instructionsTitle={heading}
      legalDisclaimer={
        <LegalDisclaimer
          provider={provider}
          pageView={PageView.AWAITING_THIRD_PARTY_AUTHENTICATION_SCREEN}
        />
      }
      userHintId={userHintId}
      footer={footer}
    />
  );
};

const mapStateToProps = (state: AppState) => {
  return {
    userHintId: state.user.idHint,
  };
};

const connector = connect(mapStateToProps, {
  setRedirectedCredentialsId: setRedirectedCredentialsIdImport,
});

type PropsFromRedux = ConnectedProps<typeof connector>;

export const PayAwaitingThirdpartyAuthenticationContainer = connector(
  PayAwaitingThirdpartyAuthenticationContainerPresenter
);
